import { useState } from "react";
import { useRetentionQueue } from "../hooks/useRetentionQueue";
import type { RetentionItem } from "../api/client";
import { moveBack, reprocess, purgeNow } from "../api/client";
import { usePipelineSteps } from "../../admin/hooks/usePipelineSteps";
import { ConfirmModal } from "@/components/ConfirmModal";
import { toast } from "@/components/Toaster";
import styles from "./RetentionQueuePage.module.css";
import { formatMinutesRemaining } from "@/lib/formatters";

/**
 * R-244 — Post-Export Retention Queue page.
 *
 * Shows all documents currently in a live post-export retention window
 * (exported but not yet purged). Operators can move a document back to
 * processing, trigger reprocessing, or purge it immediately.
 *
 * Task 9: action buttons wired to backend endpoints.
 */

function TimeRemainingCell({ minutes }: { minutes: number }) {
  const label = formatMinutesRemaining(minutes);
  const className =
    minutes <= 60
      ? styles.timeUrgent
      : minutes <= 360
        ? styles.timeWarning
        : styles.timeRemaining;
  return <span className={className}>{label}</span>;
}

// ── Move-back step picker ──────────────────────────────────────────────────

interface MoveBackPickerProps {
  item: RetentionItem;
  onClose: () => void;
  onDone: () => void;
}

function MoveBackPicker({ item, onClose, onDone }: MoveBackPickerProps) {
  const { steps, isLoading } = usePipelineSteps(item.jobTypeId);
  const [busy, setBusy] = useState(false);

  async function handleChoose(stepType: string) {
    setBusy(true);
    try {
      await moveBack(item.id, stepType);
      toast.success(`Document ${item.displayId} moved back.`);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Move back failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="text-prompt-overlay" onClick={onClose}>
      <div
        className="text-prompt-panel"
        role="dialog"
        aria-labelledby="move-back-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="move-back-title" className="text-prompt-title">
          Move back — {item.displayId}
        </h2>
        <p className="text-prompt-description">
          Choose the pipeline step to return this document to.
        </p>
        {isLoading && (
          <p className="text-prompt-description">Loading steps…</p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
          {steps.map((step) => (
            <button
              key={step.id}
              type="button"
              className="text-prompt-btn"
              disabled={busy}
              onClick={() => void handleChoose(step.stepType)}
            >
              {step.label}
            </button>
          ))}
        </div>
        <div className="text-prompt-actions">
          <button
            type="button"
            className="text-prompt-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row with wired actions ─────────────────────────────────────────────────

interface RetentionRowProps {
  item: RetentionItem;
  onMoveBack: (item: RetentionItem) => void;
  onPurge: (item: RetentionItem) => void;
  onReprocess: (item: RetentionItem) => void;
  busyReprocess: boolean;
}

function RetentionRow({ item, onMoveBack, onPurge, onReprocess, busyReprocess }: RetentionRowProps) {
  return (
    <tr>
      <td>
        {item.thumbnailPath ? (
          <img
            src={item.thumbnailPath}
            alt={`Thumbnail for ${item.filename}`}
            className={styles.thumbnail}
          />
        ) : (
          <div className={styles.thumbnailPlaceholder} aria-hidden="true" />
        )}
      </td>
      <td>
        <span className={styles.displayId}>{item.displayId}</span>
      </td>
      <td>
        <span className={styles.jobType}>{item.jobTypeName}</span>
      </td>
      <td>
        <TimeRemainingCell minutes={item.minutesRemaining} />
      </td>
      <td>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            aria-label={`Move back ${item.displayId}`}
            onClick={() => onMoveBack(item)}
          >
            Move back
          </button>
          <button
            type="button"
            className={styles.btn}
            aria-label={`Reprocess ${item.displayId}`}
            disabled={busyReprocess}
            onClick={() => onReprocess(item)}
          >
            Reprocess
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            aria-label={`Purge ${item.displayId}`}
            onClick={() => onPurge(item)}
          >
            Purge now
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function RetentionQueuePage() {
  const { items, isLoading, error, refresh } = useRetentionQueue();

  // Modal state — kept at page level so modals render outside the <table>
  // (a <div> overlay cannot be a valid child of <tbody>).
  const [moveBackTarget, setMoveBackTarget] = useState<RetentionItem | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<RetentionItem | null>(null);
  const [busyReprocessId, setBusyReprocessId] = useState<string | null>(null);

  async function handleReprocess(item: RetentionItem) {
    setBusyReprocessId(item.id);
    try {
      await reprocess(item.id);
      toast.success(`Reprocess queued for ${item.displayId}.`);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reprocess failed.");
    } finally {
      setBusyReprocessId(null);
    }
  }

  async function handlePurgeConfirm() {
    if (!purgeTarget) return;
    const target = purgeTarget;
    setPurgeTarget(null);
    try {
      await purgeNow(target.id);
      toast.success(`Images deleted for ${target.displayId}.`);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Purge failed.");
    }
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Post-Export Retention</h1>
      <p className={styles.subhead}>
        Documents in a live retention window after export. Each row will be
        automatically purged when its window expires.
      </p>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {isLoading && <p className={styles.loading}>Loading…</p>}

      {!isLoading && items.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>No retained documents</p>
          <p className={styles.emptyStateBody}>
            Documents appear here after export while their retention window is
            active.
          </p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Preview</th>
              <th scope="col">Document ID</th>
              <th scope="col">Job Type</th>
              <th scope="col">Time Remaining</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <RetentionRow
                key={item.id}
                item={item}
                onMoveBack={setMoveBackTarget}
                onPurge={setPurgeTarget}
                onReprocess={(i) => void handleReprocess(i)}
                busyReprocess={busyReprocessId === item.id}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Modals rendered outside <table> to avoid invalid DOM nesting */}
      {moveBackTarget && (
        <MoveBackPicker
          item={moveBackTarget}
          onClose={() => setMoveBackTarget(null)}
          onDone={() => {
            setMoveBackTarget(null);
            refresh();
          }}
        />
      )}

      <ConfirmModal
        open={purgeTarget !== null}
        title={`Delete images — ${purgeTarget?.displayId ?? ""}?`}
        description="Delete the images for this document? The record is kept; images cannot be recovered."
        confirmLabel="Delete images"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => void handlePurgeConfirm()}
        onCancel={() => setPurgeTarget(null)}
      />
    </div>
  );
}
